import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { addDoc, collection, doc, getDoc, getDocs } from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import {
  COLLECTION_DENSIDADE_IN_SITU,
  estadoInicialDensidadeInSitu,
  formatarDataBR,
  recalcularDensidadeInSitu,
} from "./densidadeInSituModel";
import RelatorioDensidadeInSituPrint from "./RelatorioDensidadeInSituPrint";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import {
  podeEditarCadastroDireto,
  solicitarAprovacaoCadastro,
} from "../Utils/aprovacaoCadastro";
import {
  atualizarDocumentoComMesclagem,
  extrairCamposAlterados,
} from "../Utils/concorrenciaFirestore";

function normalizar(valor) {
  return String(valor || "").toLowerCase();
}

export default function CadastroDensidadeInSitu() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [ensaios, setEnsaios] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [obrasCadastradas, setObrasCadastradas] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [open, setOpen] = useState(false);
  const [editandoItem, setEditandoItem] = useState(null);
  const [openRelatorio, setOpenRelatorio] = useState(false);
  const [codigosSelecionadosRelatorio, setCodigosSelecionadosRelatorio] = useState([]);
  const [dadosRelatorio, setDadosRelatorio] = useState(null);
  const [form, setForm] = useState(estadoInicialDensidadeInSitu);

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    setCarregandoLista(true);
    try {
      const [ensaiosSnap, clientesSnap, obrasSnap] = await Promise.all([
        getDocs(consultaTenantAtual(collection(db, COLLECTION_DENSIDADE_IN_SITU))),
        getDocs(consultaTenantAtual(collection(db, "clientes"))),
        getDocs(consultaTenantAtual(collection(db, "obras"))),
      ]);
      const ordenarNome = (a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
      const instante = (valor) => valor?.toMillis?.() ?? (valor ? new Date(valor).getTime() || 0 : 0);
      setEnsaios(ensaiosSnap.docs.map((item) => ({ ...item.data(), id: item.id }))
        .sort((a, b) => instante(b.criadoEm) - instante(a.criadoEm)));
      setClientes(clientesSnap.docs.map((item) => ({ ...item.data(), id: item.id })).sort(ordenarNome));
      setObrasCadastradas(obrasSnap.docs.map((item) => ({ ...item.data(), id: item.id })).sort(ordenarNome));
    } catch (erro) {
      console.error("Erro ao carregar cadastro de densidade in situ:", erro);
      alert("Não foi possível carregar os dados de densidade in situ. Atualize a página e tente novamente.");
    } finally {
      setCarregandoLista(false);
    }
  }

  async function carregarObras(clienteId) {
    if (!clienteId) {
      setObras([]);
      return;
    }

    setObras(obrasCadastradas.filter((item) => item.clienteId === clienteId));
  }

  function abrirNovo() {
    setEditandoItem(null);
    setObras([]);
    setForm({
      ...estadoInicialDensidadeInSitu,
      dataEnsaio: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  }

  function atualizarFormulario(parcial) {
    setForm((atual) => recalcularDensidadeInSitu({ ...atual, ...parcial }));
  }

  function selecionarCliente(cliente) {
    atualizarFormulario({
      cliente: cliente?.nome || "",
      empresa: cliente?.nome || "",
      clienteId: cliente?.id || "",
      obra: "",
      obraId: "",
    });
    carregarObras(cliente?.id);
  }

  function selecionarObra(obraId) {
    const obra = obras.find((item) => item.id === obraId);
    atualizarFormulario({
      obra: obra?.nome || "",
      obraId: obra?.id || "",
      endereco: obra?.endereco || "",
    });
  }

  async function salvar() {
    if (!form.clienteId) {
      alert("Selecione o cliente antes de salvar.");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert("Usuário não autenticado.");
      return;
    }

    const userSnap = await getDoc(doc(db, "users", user.uid));
    const userData = userSnap.data() || {};
    const tokenResult = await user.getIdTokenResult();
    const tenantIdServidor = String(userData.tenantId || tokenResult.claims?.tenantId || "").trim();
    const tenantIdAtual = String(localStorage.getItem("tenantId") || "").trim();
    const usuarioRegistro = {
      uid: user.uid,
      nome: userData.nome || user.displayName || user.email || "",
      email: user.email || "",
      tipo: userData.tipo || "",
    };
    const usuarioPermissao = {
      ...usuarioRegistro,
      tenantId: tenantIdServidor || tenantIdAtual,
      isAdmin: userData.tipo === "admin",
      isColaborador: userData.tipo === "colaborador",
    };

    const anoEnsaio = new Date(form.dataEnsaio || new Date()).getFullYear();
    const maiorSequencial = obterMaiorSequencial(ensaios, /RE-DI-(\d+)\/\d{4}/);
    const codigoLocal = `RE-DI-${String(maiorSequencial + 1).padStart(3, "0")}/${anoEnsaio}`;
    let codigo = form.codigo;

    if (!editandoItem?.id && !codigo) {
      if (!tenantIdServidor) {
        if (!tenantIdAtual) throw new Error("USUARIO_SEM_TENANT_ID");
        codigo = codigoLocal;
      } else {
        try {
          codigo = (await gerarSequencial({
            chave: "densidade_in_situ",
            escopo: anoEnsaio,
            formato: {
              padding: 3,
              ano: anoEnsaio,
              template: "RE-DI-{numero}/{ano}",
            },
            valorMinimo: maiorSequencial,
          })).codigo;
        } catch (error) {
          const erroTenant =
            String(error?.code || "").includes("failed-precondition") &&
            /tenantId/i.test(String(error?.message || ""));

          if (!erroTenant || !tenantIdAtual) throw error;

          codigo = codigoLocal;
        }
      }
    }

    const dadosParaSalvar = {
      ...recalcularDensidadeInSitu(form),
      codigo,
      atualizadoEm: new Date(),
      updatedBy: usuarioRegistro,
    };

    if (editandoItem?.id) {
      if (podeEditarCadastroDireto(usuarioPermissao, editandoItem)) {
        await atualizarDocumentoComMesclagem({
          db,
          referencia: doc(db, COLLECTION_DENSIDADE_IN_SITU, editandoItem.id),
          original: editandoItem,
          atual: { ...editandoItem, ...dadosParaSalvar },
          metadados: {
            atualizadoEm: dadosParaSalvar.atualizadoEm,
            updatedBy: dadosParaSalvar.updatedBy,
          },
        });
      } else {
        await solicitarAprovacaoCadastro({
          usuario: usuarioPermissao,
          registro: editandoItem,
          colecao: COLLECTION_DENSIDADE_IN_SITU,
          registroId: editandoItem.id,
          dados: extrairCamposAlterados(editandoItem, {
            ...editandoItem,
            ...dadosParaSalvar,
          }),
          tipo: "densidade_in_situ",
          titulo: `Alteração de densidade in situ ${editandoItem.codigo || ""}`,
        });
        alert("Alteração enviada para aprovação do administrador.");
      }
      setOpen(false);
      setEditandoItem(null);
      await carregarDados();
      return;
    }

    const docRef = await addDoc(collection(db, COLLECTION_DENSIDADE_IN_SITU), aplicarTenantAtual({
      ...dadosParaSalvar,
      criadoEm: new Date(),
      createdBy: usuarioRegistro,
      createdByUid: user.uid,
    }));

    setOpen(false);
    history.push(`/app/densidade-in-situ/${docRef.id}`);
  }

  function editar(item) {
    setEditandoItem(item);
    setForm(recalcularDensidadeInSitu({ ...estadoInicialDensidadeInSitu, ...item }));
    carregarObras(item.clienteId);
    setOpen(true);
  }

  function abrirDetalhes(item) {
    history.push(`/app/densidade-in-situ/${item.id}`);
  }

  function ensaioComEnderecoObra(item) {
    const obra = obrasCadastradas.find((obraItem) => obraItem.id === item.obraId);

    return {
      ...item,
      endereco: item.endereco || obra?.endereco || "",
    };
  }

  function abrirRelatorio() {
    setCodigosSelecionadosRelatorio(ensaiosFiltrados.map((item) => item.id));
    setOpenRelatorio(true);
  }

  function alternarCodigoRelatorio(id) {
    setCodigosSelecionadosRelatorio((atuais) =>
      atuais.includes(id) ? atuais.filter((item) => item !== id) : [...atuais, id]
    );
  }

  function gerarRelatorio() {
    const selecionados = ensaios
      .filter((item) => codigosSelecionadosRelatorio.includes(item.id))
      .map((item) => recalcularDensidadeInSitu(ensaioComEnderecoObra(item)));

    if (!selecionados.length) return;

    setDadosRelatorio({ lista: selecionados, incluirCapa: true });
    setOpenRelatorio(false);
  }

  const ensaiosFiltrados = useMemo(() => {
    const termo = normalizar(filtro);
    if (!termo) return ensaios;
    return ensaios.filter((item) =>
      [item.codigo, item.empresa, item.cliente, item.obra, item.camadaEstudo, item.operador].some((campo) =>
        contemTextoPesquisa(normalizar(campo), termo)
      )
    );
  }, [ensaios, filtro]);

  const clientesUnicos = useMemo(() => {
    const clientesPorNome = new Map();

    clientes.forEach((cliente) => {
      const nomeNormalizado = normalizar(cliente.nome).trim().replace(/\s+/g, " ");
      const chave = nomeNormalizado || `cliente-sem-nome-${cliente.id}`;
      const clienteAtual = clientesPorNome.get(chave);

      if (!clienteAtual || cliente.id === form.clienteId) {
        clientesPorNome.set(chave, cliente);
      }
    });

    return Array.from(clientesPorNome.values());
  }, [clientes, form.clienteId]);

  if (dadosRelatorio) {
    return (
      <RelatorioDensidadeInSituPrint
        dados={dadosRelatorio.lista[0] || {}}
        dadosLista={dadosRelatorio.lista}
        incluirCapa={dadosRelatorio.incluirCapa}
        onVoltar={() => setDadosRelatorio(null)}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Densidade IN SITU</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Cadastro de ensaios por frasco de areia</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovo}>+ Novo Ensaio</Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={abrirRelatorio}
            disabled={!ensaiosFiltrados.length}
          >
            Gerar relatório
          </Button>
          <TextField size="small" label="Pesquisar" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ flex: 1 }} />
          <TextField size="small" label="Qtd" value={ensaiosFiltrados.length} InputProps={{ readOnly: true }} sx={{ width: 120 }} />
        </Stack>
        </Paper>

        <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 980 }}>
            <TableHead sx={{ backgroundColor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Camada/Estudo</TableCell>
                <TableCell>Operador</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="center">Ensaios</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ensaiosFiltrados.map((item) => (
                <TableRow key={item.id} hover sx={{ cursor: "pointer" }} onClick={() => abrirDetalhes(item)}>
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.empresa || item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-secundario">{item.camadaEstudo || "-"}</TableCell>
                  <TableCell className="lista-dado-destaque">{item.operador || "-"}</TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataEnsaio)}</TableCell>
                  <TableCell align="center">{(item.ensaios || []).filter((ensaio) => ensaio.numero || ensaio.localizacao).length}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar relatório">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDadosRelatorio({
                            lista: [recalcularDensidadeInSitu(ensaioComEnderecoObra(item))],
                            incluirCapa: false,
                          });
                        }}
                      >
                        <VisibilityIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar ensaio">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          editar(item);
                        }}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao="tblDensidadeInSitu" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}
              {carregandoLista && <TableRow><TableCell colSpan={7}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && !ensaiosFiltrados.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Nenhum ensaio cadastrado.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={openRelatorio} onClose={() => setOpenRelatorio(false)} fullWidth maxWidth="md">
        <DialogTitle>Gerar relatório de densidade in situ</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }}></TableCell>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ensaiosFiltrados.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => alternarCodigoRelatorio(item.id)}
                >
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={codigosSelecionadosRelatorio.includes(item.id)}
                      onChange={() => alternarCodigoRelatorio(item.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.empresa || item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell>{formatarDataBR(item.dataEnsaio)}</TableCell>
                </TableRow>
              ))}
              {!ensaiosFiltrados.length && (
                <TableRow>
                  <TableCell colSpan={4} align="center">Nenhum código encontrado.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRelatorio(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={gerarRelatorio}
            disabled={!codigosSelecionadosRelatorio.length}
          >
            Gerar relatório
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => !salvandoCadastro && setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editandoItem ? "Editar Ensaio de Densidade IN SITU" : "Novo Ensaio de Densidade IN SITU"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Código" value={form.codigo} onChange={(e) => atualizarFormulario({ codigo: e.target.value })} fullWidth />
              <TextField label="Data do ensaio" type="date" InputLabelProps={{ shrink: true }} value={form.dataEnsaio} onChange={(e) => atualizarFormulario({ dataEnsaio: e.target.value })} fullWidth />
            </Stack>
            <Autocomplete
              options={clientesUnicos}
              getOptionLabel={(option) => option?.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={clientesUnicos.find((cliente) => cliente.id === form.clienteId) || null}
              onChange={(event, novoValor) => selecionarCliente(novoValor)}
              renderInput={(params) => <TextField {...params} label="Empresa/Cliente" fullWidth />}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField select label="Obra" value={form.obraId || ""} onChange={(e) => selecionarObra(e.target.value)} fullWidth disabled={!form.clienteId}>
                {obras.map((obra) => <MenuItem key={obra.id} value={obra.id}>{obra.nome}</MenuItem>)}
              </TextField>
              <TextField label="Camada/Estudo" value={form.camadaEstudo} onChange={(e) => atualizarFormulario({ camadaEstudo: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Operador" value={form.operador} onChange={(e) => atualizarFormulario({ operador: e.target.value })} fullWidth />
              <TextField label="Calculista" value={form.calculista} onChange={(e) => atualizarFormulario({ calculista: e.target.value })} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="contained" onClick={() => executarSalvamento(salvar)} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : editandoItem ? "Salvar alterações" : "Salvar e abrir detalhes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
